import { Controller } from "@hotwired/stimulus";
import { getCSRFToken } from "web-ui/csrf"
import { morphdomPreserveAttributes } from "web-ui/morphdom/preserve-attributes"
import * as Log from "web-ui/log"

// Connects to data-controller="show-popover"
export default class extends Controller {
  static values = {
    alignment: String,
    url: String
  }

  get popoverSelector() {
    return `[popover][data-target='${this.element.id}']`
  }

  setActive(active) {
    if (active) {
      this.element.classList.add("active")
      morphdomPreserveAttributes(this.element, ["class"])
    } else {
      this.element.classList.remove("active")
      morphdomPreserveAttributes(this.element, [])
    }
  }

  connect() {
    this.debug("Connect", { alignment: this.alignmentValue, url: this.urlValue })

    document.body.addEventListener("toggle", this.handleToggle, { capture: true })

    this.element.addEventListener("click", this.openPopover)
  }

  handleToggle = (event) => {
    if (!event.target.matches || !event.target.matches(this.popoverSelector)) {
      return
    }

    this.setActive(event.newState === "open")
  }

  openPopover = async () => {
    if (this.isPopoverOpen()) {
      return
    }

    this.setActive(true)

    const csrfToken = getCSRFToken()

    const headers = {
      "Accept": "text/vnd.turbo-stream.html",
      "X-CSRF-Token": csrfToken
    }

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("target", this.element.id)

    if (this.alignmentValue) {
      url.searchParams.set("alignment", this.alignmentValue)
    }

    try {
      const response = await fetch(url, { headers, method: "GET" })

      const responseContentType = response.headers.get("Content-Type")

      if (!this.isTurboStreamContentType(responseContentType)) {
        this.debug("Response error: ", { response })
        this.setActive(false)
        return
      }

      const html = await response.text()

      this.debug("Rendering")

      Turbo.renderStreamMessage(html)
    } catch(error) {
      this.setActive(false)
      Sentry.captureException(error)
    }
  }

  isPopoverOpen() {
    const popover = document.querySelector(this.popoverSelector)

    return !!popover
  }

  isTurboStreamContentType(contentType) {
    return contentType.match("turbo-stream.html") !== null
  }

  disconnect() {
    this.debug("Disconnect")

    const popover = document.querySelector(this.popoverSelector)

    if (popover) {
      popover.remove()
    }

    document.body.removeEventListener("toggle", this.handleToggle, { capture: true })
    this.element.removeEventListener("click", this.openPopover)
  }

  debug(message, data={}) {
    Object.assign(data, {
      id: this.element.id
    })

    Log.debug("Show Popover - " + message, data)
  }
}
